import { useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FaCheck, FaTruck, FaHandHolding, FaQrcode, FaList, FaSync, FaFilter, FaUserAlt, FaTimes, FaBoxOpen, FaChevronRight, FaSpinner } from 'react-icons/fa';
import { DateTime } from 'luxon';

import useLogisticsApi from '../../hooks/use-logistics-api';
import { useWarehouseOutgoingRangeQuery, usePartnerUsersQuery, useWarehouseOutgoingInvalidation } from '../../hooks/use-queries';
import { useDriverMap } from '../../hooks/use-driver-map';
import { useQueryBridge } from '../../hooks/use-query-bridge';
import useReader from '../../hooks/use-reader';
import { flattenOrdersToBoxes } from '../../utils/order-transforms';
import { groupBoxesByOrder } from '../../utils/box-grouping';

import AppContext from '../../store/app-context';
import AuthContext from '../../store/auth-context';

import { ActionBar, ActionButton } from '../../components/Shared/ActionBar/ActionBar';
import BarcodeDisplay from '../../components/Shared/BarcodeDisplay/BarcodeDisplay';
import { barcodeShortLabel } from '../../utils/barcode-format';

import './Dispatch.scss';
import {
    EnhancedOrderBoxWithContext,
    DpdTrackingStatus,
    getWarehouseNextStatus,
    STATUS_CODE_DISPLAY_NAMES
} from '../../types/logistics';

type DeliveryTypeFilter = 'all' | 'internal' | 'dpd';

const DPD_TRACKING_LABELS: Record<DpdTrackingStatus, string> = {
    collected: 'Collected',
    in_transit: 'In Transit',
    out_for_delivery: 'Out for Delivery',
    delivered: 'Delivered',
    failed: 'Failed',
};

interface SectionData {
    boxes: EnhancedOrderBoxWithContext[];
    completedBoxes: EnhancedOrderBoxWithContext[];
    total: number;
}

const Dispatch = () => {
    const queryClient = useQueryClient();
    const [manualMode, setManualMode] = useState(false);
    const [expandedSection, setExpandedSection] = useState<'pick' | 'holdout' | 'ready' | null>('pick');
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

    // Filter state — date defaults to 'all' to show next 3 days
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    const [selectedDate, setSelectedDate] = useState<'all' | string>('all');
    const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
    const [deliveryTypeFilter, setDeliveryTypeFilter] = useState<DeliveryTypeFilter>('all');
    const [showFilters, setShowFilters] = useState(false);

    // All boxes from API (with order context) — local state for optimistic scan updates
    const [allBoxes, setAllBoxes] = useState<EnhancedOrderBoxWithContext[]>([]);
    // Tracks which orders are currently being advanced (for loading spinner)
    const [advancingOrders, setAdvancingOrders] = useState<Set<number>>(new Set());

    const appCtx = useContext(AppContext);
    const authCtx = useContext(AuthContext);

    const { updateBoxStatus } = useLogisticsApi();
    const { invalidateWarehouseOutgoing } = useWarehouseOutgoingInvalidation();
    const { startQuagga } = useReader(null);

    // Per-box duplicate guard — prevent same box being processed concurrently
    const processingBoxIds = useRef(new Set<number>());

    // Generate date range: today + next 3 calendar days
    const dateRange = useMemo(() => {
        const dates: string[] = [];
        for (let i = 0; i <= 3; i++) {
            dates.push(DateTime.now().plus({ days: i }).toFormat('yyyy-MM-dd'));
        }
        return dates;
    }, []);

    // Server data via React Query — fetches all dates in parallel (cached, deduplicated)
    const { data: outgoingResult, isLoading: ordersLoading, isSuccess: ordersLoaded, error: ordersError } = useWarehouseOutgoingRangeQuery(dateRange);
    const rawOrders = outgoingResult?.orders;
    const dpdShipments = useMemo(() => outgoingResult?.dpdShipments ?? {}, [outgoingResult?.dpdShipments]);
    const { data: partnerUsers } = usePartnerUsersQuery(authCtx.partner?.id);

    const hasActiveFilters = selectedDate !== 'all' || selectedDriverId !== null || deliveryTypeFilter !== 'all';

    // Refs for latest values
    const allBoxesRef = useRef(allBoxes);
    const geolocationRef = useRef(appCtx.geolocation);

    useEffect(() => { allBoxesRef.current = allBoxes; }, [allBoxes]);
    useEffect(() => { geolocationRef.current = appCtx.geolocation; }, [appCtx.geolocation]);

    // Filter boxes by selected date, driver, and delivery type
    const filteredBoxes = useMemo(() => {
        let boxes = allBoxes;
        if (selectedDate !== 'all') {
            boxes = boxes.filter(b => {
                if (!b.deliveryDate) return false;
                return DateTime.fromMillis(b.deliveryDate).toFormat('yyyy-MM-dd') === selectedDate;
            });
        }
        if (selectedDriverId !== null) {
            boxes = boxes.filter(b => (b.deliveryDriverId ?? b.assignedDriverId) === selectedDriverId);
        }
        if (deliveryTypeFilter === 'dpd') {
            boxes = boxes.filter(b => b.orderId in dpdShipments);
        } else if (deliveryTypeFilter === 'internal') {
            boxes = boxes.filter(b => !(b.orderId in dpdShipments));
        }
        return boxes;
    }, [allBoxes, selectedDate, selectedDriverId, deliveryTypeFilter, dpdShipments]);

    const filteredBoxesRef = useRef(filteredBoxes);
    useEffect(() => { filteredBoxesRef.current = filteredBoxes; }, [filteredBoxes]);

    // Calculate section data
    const sections = useMemo((): { pick: SectionData; holdout: SectionData; ready: SectionData; returns: SectionData } => {
        const pickBoxes = filteredBoxes.filter(b => b.statusCode === 'WKKS' || b.statusCode === 'WKKDO');
        const holdoutBoxes = filteredBoxes.filter(b => b.statusCode === 'WKKP');
        const readyBoxes = filteredBoxes.filter(b => b.statusCode === 'WKKHO');
        const dispatchedBoxes = filteredBoxes.filter(b => b.statusCode === 'ITTC');
        const returnsBoxes = filteredBoxes.filter(b => b.statusCode === 'WC');

        return {
            pick: { boxes: pickBoxes, completedBoxes: [], total: pickBoxes.length },
            holdout: { boxes: holdoutBoxes, completedBoxes: [], total: holdoutBoxes.length },
            ready: { boxes: readyBoxes, completedBoxes: dispatchedBoxes, total: readyBoxes.length },
            returns: { boxes: returnsBoxes, completedBoxes: [], total: returnsBoxes.length }
        };
    }, [filteredBoxes]);

    // Driver name lookup (cached via React Query, shared with Incoming page)
    const { driverMap, getDriverName } = useDriverMap(partnerUsers);

    // Derive available drivers from loaded boxes (only shows drivers with actual orders)
    const availableDrivers = useMemo(() => {
        const driverIds = new Set<number>();
        allBoxes.forEach(b => {
            const driverId = b.deliveryDriverId ?? b.assignedDriverId;
            if (driverId !== null) driverIds.add(driverId);
        });
        return Array.from(driverIds).map(id => ({
            id,
            name: driverMap.get(id)?.name || `Driver #${id}`
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [allBoxes, driverMap]);

    const handleBoxScanned = useCallback(async (box: EnhancedOrderBoxWithContext) => {
        if (processingBoxIds.current.has(box.id)) return;

        const nextStatus = getWarehouseNextStatus(box.statusCode);
        if (!nextStatus) {
            appCtx.newMessage({ text: `Box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} cannot be processed in dispatch`, type: 0 });
            return;
        }
        processingBoxIds.current.add(box.id);
        const geo = geolocationRef.current;
        try {
            await updateBoxStatus(box.id, nextStatus, {
                latitude: geo.latitude ? parseFloat(geo.latitude) : undefined,
                longitude: geo.longitude ? parseFloat(geo.longitude) : undefined
            });
            setAllBoxes(prev => prev.map(b => b.id === box.id ? { ...b, statusCode: nextStatus, scanned: false } : b));
            invalidateWarehouseOutgoing();
            appCtx.newMessage({ text: `Box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} → ${STATUS_CODE_DISPLAY_NAMES[nextStatus]}`, type: 1 });
        } catch (error: any) {
            appCtx.newMessage({ text: error.message || 'Failed to update box', type: 0 });
        } finally {
            processingBoxIds.current.delete(box.id);
        }
    }, [updateBoxStatus, appCtx, invalidateWarehouseOutgoing]);

    const updateBox = useCallback(async (boxOrLocation: any) => {
        if (typeof boxOrLocation === 'string') return;
        const box = boxOrLocation as EnhancedOrderBoxWithContext;
        setAllBoxes(prev => prev.map(b => {
            if (b.id === box.id || b.barcode === box.barcode) {
                return { ...b, scanned: box.scanned };
            }
            return b;
        }));
        if (box.scanned) await handleBoxScanned(box);
    }, [handleBoxScanned]);

    // Sync query data → local allBoxes state (transform orders to flat box list).
    // Warehouse filter: order.assignedPartnerId must match the logged-in user's
    // partnerId (user.partnerId === partner.id). All warehouse staff in the same
    // partner org share one warehouse, so they see the same set of orders.
    // Orders with NULL assignedPartnerId are unassigned and excluded.
    const myPartnerId = authCtx.user?.partnerId ?? null;
    useEffect(() => {
        if (!rawOrders) return;
        const partnerOrders = myPartnerId != null
            ? rawOrders.filter(o => o.assignedPartnerId === myPartnerId)
            : rawOrders;
        setAllBoxes(flattenOrdersToBoxes(partnerOrders, ['WKKS', 'WKKDO', 'WKKP', 'WKKHO', 'ITTC', 'WC']));
    }, [rawOrders, myPartnerId]);

    // Bridge loading/error states to global overlay
    useQueryBridge(ordersLoading, ordersError as Error | null, 'Loading dispatch boxes...', 'Failed to load dispatch boxes', !!rawOrders);

    const handleStartScan = useCallback(async () => {
        setManualMode(false);
        let boxesToScan = filteredBoxesRef.current.filter(b =>
            ['WKKS', 'WKKDO', 'WKKP'].includes(b.statusCode)
        );
        if (selectedOrderId !== null) {
            boxesToScan = boxesToScan.filter(b => b.orderId === selectedOrderId);
        }
        await startQuagga({ boxes: boxesToScan, update: updateBox });
    }, [startQuagga, updateBox, selectedOrderId]);

    const handleManualToggle = useCallback((box: EnhancedOrderBoxWithContext) => {
        if (!box.scanned) {
            appCtx.newDialog({
                text: `Process box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)}?`,
                confirm: async () => { await handleBoxScanned(box); }
            });
        }
    }, [appCtx, handleBoxScanned]);

    /**
     * Advance: moves ALL boxes in a DPD order to their next status in one action.
     *
     * This is the production workflow for DPD orders — boxes are advanced as a whole
     * order rather than scanned individually. The scan and manual-per-box buttons
     * are retained only for testing/troubleshooting and will be removed in production.
     */
    const handleAdvanceOrder = useCallback((orderId: number, boxes: EnhancedOrderBoxWithContext[]) => {
        const transitionable = boxes.filter(b => getWarehouseNextStatus(b.statusCode) !== null);
        if (transitionable.length === 0) return;

        const nextStatus = getWarehouseNextStatus(transitionable[0].statusCode);
        if (!nextStatus) return;

        appCtx.newDialog({
            text: `Advance all ${transitionable.length} box${transitionable.length !== 1 ? 'es' : ''} in order #${orderId} to ${STATUS_CODE_DISPLAY_NAMES[nextStatus]}?`,
            confirm: async () => {
                setAdvancingOrders(prev => new Set(prev).add(orderId));
                const geo = geolocationRef.current;
                let successCount = 0;

                for (const box of transitionable) {
                    const target = getWarehouseNextStatus(box.statusCode);
                    if (!target) continue;
                    try {
                        await updateBoxStatus(box.id, target, {
                            latitude: geo.latitude ? parseFloat(geo.latitude) : undefined,
                            longitude: geo.longitude ? parseFloat(geo.longitude) : undefined
                        });
                        setAllBoxes(prev => prev.map(b => b.id === box.id ? { ...b, statusCode: target, scanned: false } : b));
                        successCount++;
                    } catch (error: any) {
                        appCtx.newMessage({ text: `Failed box #${box.id}: ${error.message || 'Unknown error'}`, type: 0 });
                    }
                }

                invalidateWarehouseOutgoing();
                setAdvancingOrders(prev => {
                    const next = new Set(prev);
                    next.delete(orderId);
                    return next;
                });

                if (successCount > 0) {
                    appCtx.newMessage({
                        text: `Order #${orderId}: ${successCount}/${transitionable.length} box${successCount !== 1 ? 'es' : ''} → ${STATUS_CODE_DISPLAY_NAMES[nextStatus]}`,
                        type: 1
                    });
                }
            }
        });
    }, [appCtx, updateBoxStatus, invalidateWarehouseOutgoing]);

    const clearFilters = useCallback(() => {
        setSelectedDate('all');
        setSelectedDriverId(null);
        setDeliveryTypeFilter('all');
    }, []);

    const dateFormatted = selectedDate === 'all'
        ? `Today — ${DateTime.fromISO(dateRange[dateRange.length - 1]).toFormat('d MMMM yyyy')}`
        : selectedDate === today
            ? DateTime.now().toFormat('EEEE, d MMMM yyyy')
            : DateTime.fromISO(selectedDate).toFormat('EEEE, d MMMM yyyy');

    const totalActionable = sections.pick.total + sections.holdout.total;
    const totalBoxes = totalActionable + sections.ready.total;

    const groupBoxes = useCallback((boxes: EnhancedOrderBoxWithContext[]) =>
        groupBoxesByOrder(boxes, getDriverName, 'deliveryDriverId', dpdShipments),
    [getDriverName, dpdShipments]);

    const readyHasDpd = useMemo(() =>
        sections.ready.boxes.some(b => b.orderId in dpdShipments),
    [sections.ready.boxes, dpdShipments]);

    const readyHasInternal = useMemo(() =>
        sections.ready.boxes.some(b => !(b.orderId in dpdShipments)),
    [sections.ready.boxes, dpdShipments]);

    const renderBoxCard = (box: EnhancedOrderBoxWithContext, statusClass: string, readOnly = false) => (
        <div
            key={box.id}
            className={`box-item ${!readOnly && manualMode ? 'tappable' : ''} ${readOnly ? 'read-only' : ''} ${box.scanned ? 'scanned' : ''}`}
            onClick={() => !readOnly && manualMode && handleManualToggle(box)}
        >
            <div className="barcode">
                <BarcodeDisplay barcode={box.barcode} boxId={box.id} barcodeComponents={box.barcodeComponents} />
            </div>
            {box.scanned ? (
                <div className="status-badge scanned-badge">✓ Scanned</div>
            ) : (
                <div className={`status-badge ${statusClass}`}>
                    {STATUS_CODE_DISPLAY_NAMES[box.statusCode]}
                </div>
            )}
            {box.warehouseLocation && (
                <div className="location-hint">
                    {box.warehouseLocation}
                </div>
            )}
        </div>
    );

    const sectionConfig = [
        {
            key: 'pick' as const,
            title: 'Pick',
            subtitle: 'WKKS/WKKDO → WKKP',
            icon: FaTruck,
            iconClass: 'pick',
            data: sections.pick,
            badgeClass: 'wkks',
            readOnly: false
        },
        {
            key: 'holdout' as const,
            title: 'Hold Out',
            subtitle: 'WKKP → WKKHO',
            icon: FaHandHolding,
            iconClass: 'holdout',
            data: sections.holdout,
            badgeClass: 'wkkp',
            readOnly: false
        },
        {
            key: 'ready' as const,
            title: 'Ready for Courier',
            subtitle: 'WKKHO — awaiting courier pickup',
            icon: FaBoxOpen,
            iconClass: 'load',
            data: sections.ready,
            badgeClass: 'wkkho',
            readOnly: true
        }
    ];

    return (
        <div className="warehouse-dispatch">
            {/* Header */}
            <div className="page-header">
                <div className="header-content">
                    <FaTruck className="header-icon" />
                    <div className="header-text">
                        <h1>Dispatch</h1>
                        <span className="date">{dateFormatted}</span>
                    </div>
                </div>
                <button className="btn-refresh" onClick={() => queryClient.invalidateQueries({ queryKey: ['warehouse-outgoing'] })}>
                    <FaSync />
                </button>
            </div>

            {/* Filter Bar */}
            <div className="filter-bar">
                <button
                    className={`btn-filter-toggle ${showFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <FaFilter />
                    <span>Filters</span>
                    {hasActiveFilters && <span className="filter-badge" />}
                </button>

                {showFilters && (
                    <div className="filter-controls">
                        <div className="filter-field">
                            <label>Date</label>
                            <select
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                            >
                                <option value="all">All (Next 3 days)</option>
                                {dateRange.map(d => (
                                    <option key={d} value={d}>
                                        {d === today
                                            ? `Today (${DateTime.fromISO(d).toFormat('d MMM')})`
                                            : DateTime.fromISO(d).toFormat('EEE, d MMM')}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-field">
                            <label>Delivery Courier</label>
                            <select
                                value={selectedDriverId ?? ''}
                                onChange={(e) => setSelectedDriverId(e.target.value ? Number(e.target.value) : null)}
                            >
                                <option value="">All Couriers</option>
                                {availableDrivers.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-field">
                            <label>Delivery Type</label>
                            <div className="delivery-type-toggle">
                                {(['all', 'internal', 'dpd'] as DeliveryTypeFilter[]).map(val => (
                                    <button
                                        key={val}
                                        className={`toggle-btn ${deliveryTypeFilter === val ? 'active' : ''} ${val === 'dpd' ? 'dpd' : ''}`}
                                        onClick={() => setDeliveryTypeFilter(val)}
                                    >
                                        {val === 'all' ? 'All' : val === 'internal' ? 'Internal' : 'DPD'}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {hasActiveFilters && (
                            <button className="btn-clear-filters" onClick={clearFilters}>
                                <FaTimes />
                                <span>Clear Filters</span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Summary Strip */}
            <div className="summary-strip">
                <div className="summary-item">
                    <span className="label">Total</span>
                    <span className="value">{totalBoxes}</span>
                </div>
                <div className="summary-item pick">
                    <span className="label">To Pick</span>
                    <span className="value">{sections.pick.total}</span>
                </div>
                <div className="summary-item holdout">
                    <span className="label">Hold Out</span>
                    <span className="value">{sections.holdout.total}</span>
                </div>
                <div className="summary-item load">
                    <span className="label">Ready</span>
                    <span className="value">{sections.ready.total}</span>
                </div>
            </div>

            {/* Collection Returns Banner */}
            {sections.returns.total > 0 && (
                <div className="returns-banner">
                    <FaBoxOpen className="returns-icon" />
                    <div className="returns-text">
                        <strong>{sections.returns.total} box{sections.returns.total !== 1 ? 'es' : ''} expected as returns</strong>
                        <span>ONS collection-back in progress — boxes will arrive via Incoming once courier collects.</span>
                    </div>
                </div>
            )}

            {/* Workflow Sections */}
            <div className="workflow-sections">
                {sectionConfig.map(section => (
                    <div
                        key={section.key}
                        className={`workflow-section ${expandedSection === section.key ? 'expanded' : ''} ${section.data.total === 0 ? 'complete' : ''} ${section.readOnly ? 'read-only-section' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === section.key ? null : section.key)}
                    >
                        <div className="section-header">
                            <div className={`section-icon ${section.iconClass}`}>
                                <section.icon />
                            </div>
                            <div className="section-info">
                                <h3>{section.title}</h3>
                                <span className="status-text">{section.subtitle}</span>
                            </div>
                            <div className="section-progress">
                                {section.data.total === 0 ? (
                                    <FaCheck className="complete-icon" />
                                ) : (
                                    <span className="count">{section.data.total}</span>
                                )}
                            </div>
                        </div>

                        {expandedSection === section.key && (section.data.boxes.length > 0 || section.data.completedBoxes.length > 0) && (
                            <div className="section-content" onClick={e => e.stopPropagation()}>
                                {section.readOnly && (
                                    <p className="read-only-note">
                                        {readyHasDpd && readyHasInternal
                                            ? 'Internal orders await courier pickup. DPD orders await DPD collection.'
                                            : readyHasDpd
                                            ? 'Awaiting DPD collection pickup.'
                                            : 'Reference only — courier will load these boxes.'}
                                    </p>
                                )}
                                {groupBoxes(section.data.boxes).map(group => {
                                    const isDpd = !!group.dpdShipment;
                                    const isAdvancing = advancingOrders.has(group.orderId);
                                    const canAdvance = isDpd && !section.readOnly && group.boxes.some(b => getWarehouseNextStatus(b.statusCode) !== null);
                                    const nextStatus = canAdvance ? getWarehouseNextStatus(group.boxes[0].statusCode) : null;

                                    return (
                                    <div
                                        key={group.orderId}
                                        className={`order-group ${isDpd ? 'dpd-order' : ''} ${!section.readOnly && selectedOrderId === group.orderId ? 'selected' : ''}`}
                                        onClick={() => !section.readOnly && setSelectedOrderId(selectedOrderId === group.orderId ? null : group.orderId)}
                                    >
                                        <div className="order-group-header">
                                            <div className="order-group-title">
                                                <span className="customer-name">
                                                    #{group.orderId} — {group.customerName}
                                                </span>
                                                {isDpd && (
                                                    <span className="dpd-tag">
                                                        <span className="dpd-tag-logo">DPD</span>
                                                        {group.dpdShipment?.trackingStatus && (
                                                            <span className={`dpd-tag-status tracking-${group.dpdShipment.trackingStatus}`}>
                                                                {DPD_TRACKING_LABELS[group.dpdShipment.trackingStatus]}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="group-meta">
                                                {isDpd ? <FaTruck /> : <FaUserAlt />} {group.driverName}
                                                <span className="box-count">{group.boxes.length} box{group.boxes.length !== 1 ? 'es' : ''}</span>
                                            </span>
                                            {!section.readOnly && selectedOrderId === group.orderId && (
                                                <span className="selected-badge">Selected</span>
                                            )}
                                        </div>
                                        {/* Advance button: production workflow for DPD — moves entire order to next status without scanning */}
                                        {canAdvance && (
                                            <button
                                                className={`btn-advance-order ${isAdvancing ? 'advancing' : ''}`}
                                                disabled={isAdvancing}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAdvanceOrder(group.orderId, group.boxes);
                                                }}
                                            >
                                                {isAdvancing ? (
                                                    <><FaSpinner className="spin" /> Advancing...</>
                                                ) : (
                                                    <>Advance to {STATUS_CODE_DISPLAY_NAMES[nextStatus!]} <FaChevronRight /></>
                                                )}
                                            </button>
                                        )}
                                        <div className="box-grid">
                                            {group.boxes.map((box) => renderBoxCard(box, section.badgeClass, section.readOnly))}
                                        </div>
                                    </div>
                                    );
                                })}
                                {section.data.completedBoxes.length > 0 && (
                                    <>
                                        <div className="completed-divider">
                                            <FaCheck /> <span>Dispatched</span>
                                        </div>
                                        {groupBoxes(section.data.completedBoxes).map(group => (
                                            <div key={`done-${group.orderId}`} className="order-group completed-group">
                                                <div className="order-group-header">
                                                    <span className="customer-name">#{group.orderId} — {group.customerName}</span>
                                                    <span className="group-meta">
                                                        <span className="box-count">{group.boxes.length} box{group.boxes.length !== 1 ? 'es' : ''}</span>
                                                    </span>
                                                </div>
                                                <div className="box-grid">
                                                    {group.boxes.map((box) => renderBoxCard(box, 'ittc', true))}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {totalBoxes === 0 && ordersLoaded && (
                <div className="empty-state">
                    <FaTruck className="empty-icon" />
                    <p>
                        {hasActiveFilters
                            ? selectedDate !== 'all'
                                ? `No dispatch boxes match filters for ${DateTime.fromISO(selectedDate).toFormat('d MMMM yyyy')}`
                                : 'No dispatch boxes match current filters'
                            : 'No boxes to dispatch in the next 3 days'
                        }
                    </p>
                </div>
            )}

            {/* Selected order indicator */}
            {selectedOrderId !== null && (
                <div className="selected-order-bar">
                    <span>Order #{selectedOrderId} selected</span>
                    <button onClick={() => setSelectedOrderId(null)}>
                        <FaTimes /> Clear
                    </button>
                </div>
            )}

            {/* Action Bar */}
            <ActionBar colorScheme="warn">
                <ActionButton variant="primary" icon={<FaQrcode />} onClick={handleStartScan} disabled={totalActionable === 0}>
                    {selectedOrderId !== null ? `Scan #${selectedOrderId}` : 'Scan All'}
                </ActionButton>
                <ActionButton variant={manualMode ? 'active' : 'default'} icon={<FaList />} onClick={() => setManualMode(!manualMode)} disabled={totalActionable === 0}>
                    {manualMode ? 'Exit Manual' : 'Manual'}
                </ActionButton>
            </ActionBar>
        </div>
    );
};

export default Dispatch;
